import React, { useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Grid,
  Typography,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Box,
  useMediaQuery,
} from "@mui/material";
import { tabsClasses } from "@mui/material/Tabs";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context";
import LanguageSelector from "../../../lang/LanguageSelector";
import { useTranslation } from "react-i18next";
import { getAccess } from "../../../permissions/Permissions";
import { useTheme } from "@mui/material/styles";
import useFetchObjects from "../../../api/useFetchObjects";
import { LOGO_URL } from "../../../config";

const tabStyle = {
  color: "white",
  textTransform: "capitalize",
  fontWeight: "600",
  padding: "8px 12px",
  borderRadius: "8px",
  margin: "0px 2px",
  transition: "background-color 0.3s ease, color 0.3s ease",
};

const selectedTabStyle = {
  backgroundColor: "transparent",
  borderRadius: "8px",
  color: "white",
  margin: "0px 2px",
  fontWeight: "700",
  border: "3px solid white",
  borderBottom: "5px solid white",
};

const NavBar = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user;

  const { data } = useFetchObjects(
    ["permissions-users"],
    `users/${user?.user?.id}/`,
    token
  );

  const transformPermissions = (data) => {
    if (!data) return {};

    return {
      pharmacy: data.pharmacy || false,
      finance: data.finance || false,
      hr: data.hr || false,
    };
  };

  // const permissions = transformPermissions(data);

  const firstname = user?.user?.username;
  const permissions = getAccess(
    transformPermissions(data),
    user?.user?.isAdmin
  );

  const [value, setValue] = useState("/reception");
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogoutClick = () => {
    auth.logout();
    navigate("/user/sign-in/");
    handleMenuClose();
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    navigate(newValue);
  };

  useEffect(() => {
    const topLevelPath = location.pathname.split("/")[1];
    setValue(`/${topLevelPath}`);
  }, [location.pathname]);

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: theme.palette.primary,
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        minHeight: "10vh", // Reduced height
        padding: isSmallScreen ? "8px" : "7px",
      }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ paddingX: isSmallScreen ? "8px" : "16px", height: "100%" }}
      >
        {/* Row for Profile and Language Selector */}
        <Grid
          item
          xs={12}
          md={1}
          display={"flex"}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <img
              src={LOGO_URL}
              alt="Company Logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/images/logo/logo.png";
              }}
              style={{
                height: isExtraSmallScreen ? "50px" : "70px", // Responsive logo height
                width: "auto",
                borderRadius: "5px",
                backgroundColor: "white",
                padding: "10px", // Reduced padding
              }}
            />
          </Box>

          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <LanguageSelector />
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: isExtraSmallScreen ? "0.875rem" : "1rem",
                  margin: "0px 5px",
                }} // Responsive font size
              >
                {firstname}
              </Typography>
              <Avatar
                onClick={handleMenuClick}
                sx={{
                  cursor: "pointer",
                  boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)",
                  height: isExtraSmallScreen ? "30px" : "40px", // Responsive size
                  width: isExtraSmallScreen ? "30px" : "40px",
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Row for Tabs */}
        <Grid item xs={12} md={9}>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              indicatorColor="none"
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  "&.Mui-disabled": { opacity: 0.3 },
                },
              }}
            >
              {permissions.pharmacy && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/pharmacy" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.pharmacy")}
                  value="/pharmacy"
                  component={NavLink}
                  to="/pharmacy"
                />
              )}

              {permissions.finance && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/finance" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.finance")}
                  value="/finance"
                  component={NavLink}
                  to="/finance"
                />
              )}
              {permissions.hr && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/human-resource" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.hr")}
                  value="/human-resource"
                  component={NavLink}
                  to="/human-resource"
                />
              )}
            </Tabs>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={2}
          sx={{ display: { xs: "none", md: "flex" } }}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <LanguageSelector />
          <Typography
            variant="body2"
            sx={{
              color: "white",
              fontWeight: "bold",
              fontSize: isExtraSmallScreen ? "0.875rem" : "1rem",
              margin: "0px 5px",
            }} // Responsive font size
          >
            {firstname}
          </Typography>
          <Avatar
            onClick={handleMenuClick}
            sx={{
              cursor: "pointer",
              boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)",
              height: isExtraSmallScreen ? "30px" : "40px", // Responsive size
              width: isExtraSmallScreen ? "30px" : "40px",
            }}
          />
        </Grid>
      </Grid>

      {/* Menu for Avatar */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <MenuItem onClick={handleLogoutClick} sx={{ fontWeight: "500" }}>
          {t("navbar.logout")}
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default NavBar;
